import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { BuildxConfig, ApiHeaders, ErrorResponse } from "../types";

/**
 * Base service class that provides common functionality for all Buildx services
 */
export class BaseService {
	public config: BuildxConfig;
	public axiosInstance: AxiosInstance;
	private _accessToken: string | null = null;
	private _refreshToken: string | null = null;

	constructor(config: BuildxConfig) {
		this.config = config;
		this.axiosInstance = axios.create({
			baseURL: config.apiEndpoint,
			timeout: 30000,
		});

		// Add request interceptor for authentication
		this.axiosInstance.interceptors.request.use(
			(config) => {
				// Add API key to all requests
				config.headers["X-API-Key"] = this.config.apiKey;
				return config;
			},
			(error) => {
				return Promise.reject(error);
			}
		);

		// Add response interceptor for error handling
		this.axiosInstance.interceptors.response.use(
			(response) => response,
			(error) => {
				return this.handleError(error);
			}
		);
	}

	/**
	 * Update the service configuration
	 */
	updateConfig(config: BuildxConfig): void {
		this.config = config;
		this.axiosInstance.defaults.baseURL = config.apiEndpoint;
	}

	/**
	 * Set the access token
	 */
	public setAccessToken(token: string | null): void {
		this._accessToken = token;
	}

	/**
	 * Get the access token
	 */
	public getAccessToken(): string | null {
		return this._accessToken;
	}

	/**
	 * Set the refresh token
	 */
	public setRefreshToken(token: string | null): void {
		this._refreshToken = token;
	}

	/**
	 * Get the refresh token
	 */
	public getRefreshToken(): string | null {
		return this._refreshToken;
	}

	/**
	 * Clear all stored tokens
	 */
	public clearTokens(): void {
		this._accessToken = null;
		this._refreshToken = null;
	}

	/**
	 * Check if user is authenticated (has access token)
	 */
	public isAuthenticated(): boolean {
		return this._accessToken !== null;
	}

	/**
	 * Get default headers with authentication
	 */
	public async getHeaders(useRefreshToken = false): Promise<ApiHeaders> {
		const token = useRefreshToken ? this._refreshToken : this._accessToken;

		const headers: ApiHeaders = {
			"Content-Type": "application/json",
			"X-API-Key": this.config.apiKey,
		};

		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}

		return headers;
	}

	/**
	 * Get headers without authentication (for public endpoints)
	 */
	public getPublicHeaders(): ApiHeaders {
		return {
			"Content-Type": "application/json",
			"X-API-Key": this.config.apiKey,
		};
	}

	/**
	 * Make a GET request
	 */
	public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
		const headers = await this.getHeaders();
		const response: AxiosResponse<T> = await this.axiosInstance.get(url, {
			...config,
			headers: { ...headers, ...config?.headers },
		});
		return response.data;
	}

	/**
	 * Make a POST request
	 */
	public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
		const headers = await this.getHeaders();
		const response: AxiosResponse<T> = await this.axiosInstance.post(url, data, {
			...config,
			headers: { ...headers, ...config?.headers },
		});
		return response.data;
	}

	/**
	 * Make a PUT request
	 */
	public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
		const headers = await this.getHeaders();
		const response: AxiosResponse<T> = await this.axiosInstance.put(url, data, {
			...config,
			headers: { ...headers, ...config?.headers },
		});
		return response.data;
	}

	/**
	 * Make a PATCH request
	 */
	public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
		const headers = await this.getHeaders();
		const response: AxiosResponse<T> = await this.axiosInstance.patch(url, data, {
			...config,
			headers: { ...headers, ...config?.headers },
		});
		return response.data;
	}

	/**
	 * Make a DELETE request
	 */
	public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
		const headers = await this.getHeaders();
		const response: AxiosResponse<T> = await this.axiosInstance.delete(url, {
			...config,
			headers: { ...headers, ...config?.headers },
		});
		return response.data;
	}

	/**
	 * Handle API errors
	 */
	public handleError(error: any): ErrorResponse {
		if (error.response) {
			const { status, data } = error.response;

			if (data && typeof data === "object") {
				return {
					error: data.error || "RequestError",
					message: data.message || data.description || "Something went wrong",
					statusCode: status,
					success: false,
				};
			}

			return {
				error: "RequestError",
				message: `Request failed with status ${status}`,
				statusCode: status,
				success: false,
			};
		}

		if (error.message === "Network Error") {
			return {
				error: "NetworkError",
				message: "Network connection failed",
				statusCode: 500,
				success: false,
			};
		}

		return {
			error: "RequestError",
			message: error.message || "Unknown error occurred",
			statusCode: 500,
			success: false,
		};
	}

	/**
	 * Build URL with project ID
	 */
	public buildProjectUrl(projectId?: string, path = ""): string {
		const pid = projectId || this.config.projectId;
		if (!pid) {
			throw new Error("Project ID is required");
		}
		return `/project/${pid}${path}`;
	}

	/**
	 * Build URL with organization ID
	 */
	public buildOrgUrl(organizationId?: string, path = ""): string {
		const oid = organizationId || this.config.organizationId;
		if (!oid) {
			throw new Error("Organization ID is required");
		}
		return `/${oid}${path}`;
	}

	/**
	 * Build query string from options
	 */
	public buildQueryString(options: Record<string, any>): string {
		const params = new URLSearchParams();

		Object.entries(options).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				if (typeof value === "object") {
					params.append(key, JSON.stringify(value));
				} else {
					params.append(key, String(value));
				}
			}
		});

		const queryString = params.toString();
		return queryString ? `?${queryString}` : "";
	}
}